/**
 * FilesTransformPage — выбор листа + переименование колонок + выбор колонок.
 *
 * URL: /files/transform?id={dataset_id}
 * Если id не указан — показываем подсказку "сначала загрузите файл".
 *
 * Phase 2.4 (TZ 2026-08-07) — Files Transform UX:
 *  - **Compact top action bar**: file type/name/status + sheet picker +
 *    rows/cols/errors metrics + LibraryBar + Apply/Back/Download/Save
 *    в ОДНОЙ строке (sticky).
 *  - **Explicit Save contract**: rename/select/sheet changes — draft-only;
 *    Save делает одну явную операцию, персистящую весь Transform config
 *    (sheet + renames + selected columns). НЕТ fire-and-forget PATCH на
 *    каждый чекбокс / blur / sheet click.
 *  - **Sheet picker**: выбор листа — local state only, не триггерит reparse
 *    / новый Dataset; персистится ТОЛЬКО при Save.
 *  - **Rename UX**: click на имя открывает input, draft предзаполнен,
 *    Enter/Blur commit, Esc cancel. Пустое имя запрещено. Дубликаты
 *    имён — inline error и блок Save.
 *  - **Preview 20/50/100/Все**: переключатель в шапке превью, лимит
 *    пробрасывается в `useColumnPreview(datasetId, limit)`. `limit=0`
 *    = «Все» (Phase 2.4 backend support).
 *  - **Unsaved changes**: beforeunload + SPA route blocker через
 *    `useUnsavedChanges(isDirty)`.
 *  - **LibraryBar** (kind=files_transform): integrate в верхней панели,
 *    `applyRequest={{ dataset_id }}`, save-applies fire 3 PATCH-а после
 *    apply-ответа (одна явная операция).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  LayoutList,
  Loader2,
  Pencil,
  RotateCw,
  Save,
  Search,
  Table2,
  XCircle,
} from 'lucide-react';

import {
  useColumnPreview,
  type ColumnPreviewLimit,
  useDataset,
  useDatasetPreview,
  useDatasetSheets,
  useProjectDatasets,
  useRenameColumns,
  useReparseDataset,
  useSelectColumns,
  useSelectSheet,
} from '@/hooks/useFiles';
import { useDownloadDataset } from '@/hooks/useDownloadDataset';
import { DownloadButton } from '@/components/files/DownloadButton';
import { DataTable } from '@/components/shared/DataTable';
import { type ColumnDef } from '@tanstack/react-table';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useFilesTransformStore } from '@/store/filesTransformStore';
import { useProjectStore } from '@/store/projectStore';
import { cn } from '@/lib/utils';

// === Helpers ===

/**
 * Нормализует server.selected_columns (None = все / [] = пусто) в Set.
 * `selected_columns` на backend:
 *   - null  → «все колонки» (None)
 *   - []    → «ничего не выбрано» (явный пустой массив)
 *   - [...] → конкретный subset
 */
function serverSelectedToSet(
  raw: string[] | null | undefined,
  allColumns: string[],
): Set<string> {
  if (raw === null || raw === undefined) {
    return new Set(allColumns); // null = «все»
  }
  return new Set(raw);
}

/** Эффективный список колонок для UI: из original_columns применяя renames. */
function effectiveColumnsFor(
  originalColumns: string[],
  renames: Record<string, string>,
): string[] {
  return originalColumns.map((c) => renames[c] ?? c);
}

/**
 * Находит дубликаты новых имён в renames (Phase 2.4: ошибка 422 на бэке,
 * но показываем заранее в UI, чтобы Save не дёргал запрос впустую).
 */
function findDuplicateRenameTargets(
  renames: Record<string, string>,
  originalColumns: string[],
): string[] {
  const counts = new Map<string, number>();
  for (const orig of originalColumns) {
    const eff = renames[orig] ?? orig;
    counts.set(eff, (counts.get(eff) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .filter(([, n]) => n > 1)
    .map(([name]) => name);
}

// === Component ===

export function FilesTransformPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const datasetId = searchParams.get('id');

  const { data: dataset } = useDataset(datasetId);
  const { data: sheets } = useDatasetSheets(datasetId);
  const { data: preview } = useDatasetPreview(datasetId);
  // Phase 2.4: preview limit 20/50/100/0 (Все).
  const [previewLimit, setPreviewLimit] = useState<ColumnPreviewLimit>(20);
  const { data: colPreview } = useColumnPreview(datasetId, previewLimit);

  // Колонки превью-таблицы для <DataTable> (эффективные колонки после
  // переименований; значения из displayed_data, сложные типы — JSON).
  // useMemo — здесь, в теле компонента: хук не может жить внутри JSX-IIFE.
  type PreviewRow = {
    raw_data: Record<string, unknown>;
    displayed_data: Record<string, unknown>;
  };
  const previewColumns = useMemo<ColumnDef<PreviewRow>[]>(() => {
    return (colPreview?.effective_columns ?? []).map((columnName) => ({
      id: columnName,
      accessorFn: (row) => row.displayed_data[columnName],
      header: columnName,
      cell: (info) => {
        const v = info.getValue<unknown>();
        const display =
          v === null || v === undefined
            ? ''
            : typeof v === 'string'
              ? v
              : JSON.stringify(v);
        return display;
      },
    }));
  }, [colPreview]);
  const selectSheet = useSelectSheet();
  const renameColumns = useRenameColumns();
  const selectColumns = useSelectColumns();
  const reparseMutation = useReparseDataset();

  // Phase 2.5 (ТЗ §4): когда `?id` нет, показываем queue — список datasets
  // проекта с нетерминальными (uploaded/parsing) и failed статусами, а не
  // «сначала загрузите файл». Иначе пользователь не понимает, что уже идёт
  // обработка.
  const storeProjectId = useProjectStore((s) => s.currentProjectId);
  const { data: projectDatasets } = useProjectDatasets(
    datasetId ? null : storeProjectId,
  );

  // === Draft state (P1.4: поднят в filesTransformStore — единая workspace-
  //     библиотека применяет/сохраняет files_transform-секцию через store) ===
  const pickedSheet = useFilesTransformStore((s) => s.pickedSheet);
  const renames = useFilesTransformStore((s) => s.renames);
  const selectedColumns = useFilesTransformStore((s) => s.selectedColumns);
  const setPickedSheet = useFilesTransformStore((s) => s.setPickedSheet);
  const setRenames = useFilesTransformStore((s) => s.setRenames);
  const setSelectedColumns = useFilesTransformStore((s) => s.setSelectedColumns);
  // UI использует Set для membership-проверок — дерайвим из store array.
  const selected = useMemo(() => new Set(selectedColumns), [selectedColumns]);
  const [filter, setFilter] = useState('');
  // Phase 2.2: download state.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const download = useDownloadDataset();

  // originalColumns из raw_metadata.
  const originalColumns = useMemo<string[]>(() => {
    const meta = dataset?.raw_metadata as { original_columns?: string[] } | null;
    return meta?.original_columns ?? [];
  }, [dataset?.raw_metadata]);

  // === Sync local state from server (на загрузку dataset'а и при смене dataset) ===
  // Phase 2.4: серверный snapshot приходит ОДИН раз при mount / dataset change.
  // После этого local state — ЕДИНСТВЕННЫЙ источник правды до Save.
  const lastSyncedDatasetId = useRef<string | null>(null);
  useEffect(() => {
    if (!dataset) return;
    if (lastSyncedDatasetId.current === dataset.id) return;
    lastSyncedDatasetId.current = dataset.id;
    setRenames(dataset.column_renames ?? {});
    setSelectedColumns(
      Array.from(serverSelectedToSet(dataset.selected_columns, [])),
    );
  }, [dataset, setRenames, setSelectedColumns]);

  // Sync suggested sheet только при первой загрузке sheets (Phase 2.4: не
  // затираем пользовательский выбор при invalidate).
  const sheetSyncedRef = useRef(false);
  useEffect(() => {
    if (!sheets || sheetSyncedRef.current) return;
    if (sheets.suggested_label) {
      setPickedSheet(sheets.suggested_label);
    }
    sheetSyncedRef.current = true;
  }, [sheets, setPickedSheet]);

  // Reset sheet sync ref при смене dataset.
  useEffect(() => {
    sheetSyncedRef.current = false;
    lastSyncedDatasetId.current = null;
  }, [datasetId]);

  // Когда приходит dataset.selected_columns для нового dataset — заполняем Set
  // реальными effective columns (Phase 2.4: см. serverSelectedToSet с allColumns).
  useEffect(() => {
    if (!dataset) return;
    if (lastSyncedDatasetId.current !== dataset.id) return;
    // Применяем только если ещё не выставили (server selected_columns нормализуется
    // через effective columns с учётом renames).
    setSelectedColumns(
      Array.from(
        serverSelectedToSet(
          dataset.selected_columns,
          effectiveColumnsFor(originalColumns, renames),
        ),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalColumns.length]);

  // === isDirty ===
  // Сравниваем local draft с server snapshot.
  const isDirty = useMemo(() => {
    if (!dataset) return false;
    // Sheet — сравниваем pickedSheet с raw_metadata.selected_sheet (если есть)
    // или с sheets.suggested_label как baseline.
    const serverSheet =
      (dataset.raw_metadata as { selected_sheet?: string } | null)?.selected_sheet ??
      sheets?.suggested_label ??
      null;
    if ((pickedSheet ?? null) !== (serverSheet ?? null)) return true;
    // Renames — JSON-stringify для порядка ключей.
    const serverRenames = dataset.column_renames ?? {};
    if (JSON.stringify(renames) !== JSON.stringify(serverRenames)) return true;
    // Selected columns — нормализуем server в Set и сравниваем.
    const serverSelected = serverSelectedToSet(
      dataset.selected_columns,
      effectiveColumnsFor(originalColumns, serverRenames),
    );
    if (selected.size !== serverSelected.size) return true;
    for (const v of selected) if (!serverSelected.has(v)) return true;
    return false;
  }, [dataset, pickedSheet, sheets?.suggested_label, renames, selected, originalColumns]);

  // === Validation ===
  const validationErrors = useMemo<string[]>(() => {
    const errs: string[] = [];
    // Empty rename
    for (const [orig, target] of Object.entries(renames)) {
      if (!target.trim()) {
        errs.push(
          t('files.transform.rename_empty', {
            defaultValue: 'Пустое имя для колонки «{{name}}»',
            name: orig,
          }),
        );
      }
    }
    // Duplicate rename targets
    const dups = findDuplicateRenameTargets(renames, originalColumns);
    if (dups.length > 0) {
      errs.push(
        t('files.transform.rename_duplicates', {
          defaultValue: 'Дублирующиеся имена колонок: {{names}}',
          names: dups.join(', '),
        }),
      );
    }
    return errs;
  }, [renames, originalColumns, t]);

  const canSave =
    isDirty &&
    validationErrors.length === 0 &&
    (renameColumns.isIdle && selectColumns.isIdle && selectSheet.isIdle) &&
    dataset?.status === 'ready';

  // === Unsaved changes guard ===
  useUnsavedChanges(
    isDirty,
    t('files.transform.unsaved_confirm', {
      defaultValue: 'Есть несохранённые изменения. Уйти без сохранения?',
    }),
  );

  // === P1.4: Library integration перенесена в единую workspace-библиотеку
  //     (Header → WorkspaceLibraryBar). files_transform-секция применяется
  //     через filesTransformStore setters — те же, что использует Save ниже. ===

  // === Empty / loading guards ===
  if (!datasetId) {
    // Phase 2.5: вместо тупика «сначала загрузите файл» показываем queue.
    const items = projectDatasets?.items ?? [];
    const pending = items.filter(
      (d) => d.status === 'uploaded' || d.status === 'parsing',
    );
    const failed = items.filter((d) => d.status === 'failed');
    const readyCount = items.filter((d) => d.status === 'ready').length;

    return (
      <div className="space-y-4" data-testid="transform-queue">
        <div>
          <h1 className="text-2xl font-semibold mb-1">
            {t('files.transform.queue_title', {
              defaultValue: 'Трансформація файлів',
            })}
          </h1>
          <p className="text-sm text-surface-400">
            {t('files.transform.queue_subtitle', {
              defaultValue:
                'Виберіть файл, який ще в обробці, або перейдіть у вже готовий.',
            })}
          </p>
        </div>

        {items.length === 0 && (
          <div className="card text-sm text-surface-400 text-center py-8">
            {t('files.transform.no_dataset')}
          </div>
        )}

        {pending.length > 0 && (
          <div className="card border-info-tint/20 bg-info-tint/5">
            <h2 className="text-sm font-semibold text-info300 mb-2 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('files.transform.queue_processing', {
                defaultValue: 'В обробці',
              })}
              <span className="text-xs text-surface-500">
                ({pending.length})
              </span>
            </h2>
            <ul className="space-y-1.5">
              {pending.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="flex-1 min-w-0 truncate">{d.name}</span>
                  <span className="text-xs text-info300 uppercase tracking-wider">
                    {t(`files.status.${d.status}`)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/files/transform?id=${d.id}`)
                    }
                    className="btn-ghost text-xs"
                    aria-label={t('files.list.open', {
                      defaultValue: 'Відкрити',
                    })}
                    data-testid="transform-queue-open-pending"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {failed.length > 0 && (
          <div className="card border-danger-tint/20 bg-danger-tint/5">
            <h2 className="text-sm font-semibold text-danger mb-2 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              {t('files.transform.queue_failed', {
                defaultValue: 'Не вдалося обробити',
              })}
              <span className="text-xs text-surface-500">
                ({failed.length})
              </span>
            </h2>
            <ul className="space-y-2">
              {failed.map((d) => (
                <li
                  key={d.id}
                  className="flex items-start gap-2 text-sm"
                >
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{d.name}</p>
                    {d.error_message && (
                      <p className="text-xs text-danger mt-0.5 whitespace-normal break-words">
                        {d.error_message}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => reparseMutation.mutate(d.id)}
                    className="btn-ghost text-xs flex-shrink-0"
                    disabled={reparseMutation.isPending}
                    aria-label={t('files.list.retry', {
                      defaultValue: 'Повторити',
                    })}
                    data-testid="transform-queue-retry"
                  >
                    {reparseMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RotateCw className="w-3.5 h-3.5" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {items.length > 0 && pending.length === 0 && failed.length === 0 && (
          <div className="card text-sm text-surface-300 text-center py-6 border-success-tint/20 bg-success-tint/5">
            <CheckCircle2 className="w-5 h-5 text-success mx-auto mb-2" />
            <p>
              {t('files.transform.queue_all_ready', {
                defaultValue: 'Усі файли вже оброблені.',
              })}
            </p>
            <button
              type="button"
              onClick={() => navigate('/files/results')}
              className="btn-ghost text-xs mt-3 border border-success-tint/30 hover:bg-success-tint/10"
            >
              {t('files.list.go_results', {
                defaultValue: 'Результати',
              })}
            </button>
            {readyCount > 0 && (
              <p className="text-xs text-surface-500 mt-2">
                {readyCount}{' '}
                {readyCount === 1
                  ? t('files.transform.queue_ready_one', {
                      defaultValue: 'готовий файл',
                    })
                  : t('files.transform.queue_ready_many', {
                      defaultValue: 'готових файлів',
                    })}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
  if (!dataset || !preview) {
    return (
      <div className="card text-sm text-surface-400 flex items-center gap-2 justify-center py-8">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('files.transform.loading', { defaultValue: 'Загружаем файл…' })}
      </div>
    );
  }

  const isBusy = dataset.status === 'uploaded' || dataset.status === 'parsing';
  const isReparsing = isBusy;
  const isFailed = dataset.status === 'failed';
  const errorCount =
    (dataset.raw_metadata as { error_count?: number } | null)?.error_count ?? 0;

  // === Handlers ===
  const handleBack = () => {
    if (isDirty) {
      const ok = window.confirm(
        t('files.transform.unsaved_confirm', {
          defaultValue: 'Есть несохранённые изменения. Уйти без сохранения?',
        }),
      );
      if (!ok) return;
    }
    navigate('/files/upload');
  };

  /**
   * Phase 2.4 explicit Save contract: одна явная операция персистит
   *   1) selected_sheet (PATCH /datasets/{id}/sheets — reparse внутри)
   *   2) column_renames (PATCH /datasets/{id}/columns)
   *   3) selected_columns (PATCH /datasets/{id}/selected-columns)
   *
   * НЕТ fire-and-forget на toggle/blur — Save — единственный момент,
   * когда local draft едет на сервер.
   */
  const handleSave = async () => {
    if (!canSave) return;
    const promises: Promise<unknown>[] = [];
    // 1) Sheet (если менялся)
    const serverSheet =
      (dataset.raw_metadata as { selected_sheet?: string } | null)?.selected_sheet ??
      sheets?.suggested_label ??
      null;
    if ((pickedSheet ?? null) !== (serverSheet ?? null) && pickedSheet) {
      promises.push(
        selectSheet
          .mutateAsync({ datasetId, selectedSheet: pickedSheet, reparse: true })
          .catch(() => undefined),
      );
    }
    // 2) Renames
    const serverRenames = dataset.column_renames ?? {};
    if (JSON.stringify(renames) !== JSON.stringify(serverRenames)) {
      // Фильтруем пустые target'ы (UI уже подсветил, но на всякий случай).
      const safeRenames: Record<string, string> = {};
      for (const [k, v] of Object.entries(renames)) {
        if (v.trim()) safeRenames[k] = v.trim();
      }
      promises.push(
        renameColumns
          .mutateAsync({ datasetId, renames: safeRenames })
          .catch(() => undefined),
      );
    }
    // 3) Selected columns
    const serverSelectedArr = effectiveColumnsFor(
      originalColumns,
      serverRenames,
    ).filter((c) => serverSelectedToSet(dataset.selected_columns, []).has(c));
    const localSelectedArr = Array.from(selected);
    const sameSelection =
      serverSelectedArr.length === localSelectedArr.length &&
      localSelectedArr.every((c) => serverSelectedArr.includes(c));
    if (!sameSelection) {
      promises.push(
        selectColumns
          .mutateAsync({ datasetId, selectedColumns: localSelectedArr })
          .catch(() => undefined),
      );
    }
    if (promises.length > 0) {
      await Promise.all(promises);
    }
    // После Save — переход на /files/results.
    navigate('/files/results');
  };

  // Phase 2.2: скачать текущий dataset (server-truth — Save ещё не нажат,
  // но draft-only — для скачивания используем последний persisted state).
  const handleDownload = async () => {
    try {
      setDownloadError(null);
      await download.mutateAsync(datasetId);
    } catch (err) {
      setDownloadError(
        err instanceof Error
          ? err.message
          : t('files.results.download_error', {
              defaultValue: 'Не удалось скачать файл',
            }),
      );
    }
  };

  // === Sheet change (Phase 2.4: только local state, без PATCH) ===
  const handleSheetClick = (label: string) => {
    if (isBusy || selectSheet.isPending) return;
    setPickedSheet(label);
  };

  // === Column toggle (Phase 2.4: только store state, без PATCH) ===
  const handleColumnToggle = (effective: string, checked: boolean) => {
    const cur = useFilesTransformStore.getState().selectedColumns;
    if (checked) {
      if (!cur.includes(effective)) setSelectedColumns([...cur, effective]);
    } else {
      setSelectedColumns(cur.filter((c) => c !== effective));
    }
  };

  // === Rename commit (Phase 2.4: только store state, без PATCH) ===
  const handleRenameCommit = (orig: string, newName: string) => {
    const trimmed = newName.trim();
    const prev = useFilesTransformStore.getState().renames;
    const next = { ...prev };
    if (!trimmed || trimmed === orig) {
      delete next[orig];
    } else {
      next[orig] = trimmed;
    }
    setRenames(next);
  };

  // === Compact top action bar (Phase 2.4) ===
  const compactActionBar = (
    <div className="card sticky top-2 z-10 flex items-center gap-2 flex-wrap min-w-0 py-2 px-3">
      {/* 1. file icon + name + status */}
      <div className="flex items-center gap-2 min-w-0 shrink-0 max-w-[18rem]">
        <div className="w-7 h-7 rounded-md bg-surface-800/60 flex items-center justify-center shrink-0">
          {isBusy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-400" />
          ) : isFailed ? (
            <XCircle className="w-3.5 h-3.5 text-danger" />
          ) : (
            <CheckCircle2 className="w-3.5 h-3.5 text-success" />
          )}
        </div>
        <div className="min-w-0">
          <div
            className="text-xs font-medium truncate"
            title={dataset.original_filename}
          >
            {dataset.original_filename}
          </div>
          <div className="text-[10px] text-surface-500 uppercase tracking-wider">
            {dataset.file_type}
          </div>
        </div>
      </div>

      {/* 2. metrics: rows / cols / errors (compact) */}
      <div className="flex items-center gap-1.5 shrink-0 text-[10px]">
        <CompactMetric
          icon={LayoutList}
          label={t('files.transform.totalRows', { defaultValue: 'Рядов' })}
          value={dataset.total_rows.toLocaleString()}
        />
        <CompactMetric
          icon={Table2}
          label={t('files.transform.totalCols', { defaultValue: 'Колонок' })}
          value={originalColumns.length}
        />
        <CompactMetric
          icon={AlertCircle}
          label={t('files.transform.validErrors', { defaultValue: 'Ошибок' })}
          value={errorCount.toLocaleString()}
          tone={errorCount > 0 ? 'warn' : 'muted'}
        />
      </div>

      {/* 3. sheet picker (compact inline) */}
      {sheets && sheets.groups.length > 0 && (
        <div className="flex items-center gap-1 shrink-0">
          {sheets.groups.map((g) => {
            const isSelected = g.label === pickedSheet;
            const isSuggested = g.label === sheets.suggested_label;
            return (
              <button
                key={g.label}
                onClick={() => handleSheetClick(g.label)}
                disabled={isBusy}
                className={cn(
                  'text-[11px] px-2 py-0.5 rounded border transition-colors shrink-0',
                  isSelected
                    ? 'border-primary-500 bg-primary-500/15 text-primary-200'
                    : 'border-surface-700 hover:border-surface-600 text-surface-300',
                  isBusy && 'opacity-50 cursor-not-allowed',
                )}
                title={
                  g.members.length > 1
                    ? `${t('files.transform.merges', {
                        defaultValue: 'Объединяет:',
                      })} ${g.members.join(', ')}`
                    : g.label
                }
                data-testid={`transform-sheet-${g.label}`}
              >
                {g.label}
                {isSuggested && (
                  <span className="ml-0.5 text-[8px] text-surface-500">★</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* spacer */}
      <div className="flex-1" />

      {/* 4. dirty indicator */}
      {isDirty && (
        <span
          className="w-2 h-2 rounded-full bg-warning-tint shrink-0"
          title={t('files.transform.unsaved', {
            defaultValue: 'Есть несохранённые изменения',
          })}
          data-testid="transform-dirty-indicator"
        />
      )}

      {/* 5. action buttons */}
      <button
        type="button"
        onClick={handleBack}
        className="btn-ghost px-2 py-1 text-xs"
        data-testid="transform-back-button"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span>{t('common.back', { defaultValue: 'Назад' })}</span>
      </button>
      {/* Phase 4: единый DownloadButton (variant="primary") в action bar.
          Disabled-state логика вынесена в компонент: готов/обработка/failed/
          timeout. Error отображается inline ниже (existing UI). */}
      <DownloadButton
        dataset={dataset}
        onDownload={handleDownload}
        isDownloading={download.isPending}
        variant="ghost"
        label={t('files.results.act_download', { defaultValue: 'Скачать' })}
        testId="transform-download-button"
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={!canSave}
        className={cn(
          'btn text-xs px-3 py-1',
          canSave
            ? 'bg-primary-500 hover:bg-primary-400 text-white'
            : 'bg-surface-800 text-surface-500 cursor-not-allowed',
        )}
        title={
          validationErrors.length > 0
            ? validationErrors.join('\n')
            : t('files.transform.save_title', {
                defaultValue: 'Сохранить все изменения',
              })
        }
        data-testid="transform-save-button"
      >
        <Save className="w-3.5 h-3.5" />
        <span className="font-semibold">
          {t('files.transform.save', { defaultValue: 'Сохранить' })}
        </span>
      </button>

      {/* P1.4: LibraryBar (kind=files_transform) убран — единая workspace-
          библиотека теперь в Header (WorkspaceLibraryBar). */}
    </div>
  );

  // === Render ===
  return (
    <div className="space-y-3 min-w-0">
      {/* ===== Compact top action bar ===== */}
      {compactActionBar}

      {/* ===== Inline validation errors ===== */}
      {validationErrors.length > 0 && (
        <div
          className="text-xs text-danger bg-danger-tint/10 rounded p-2 border border-danger-tint/30"
          data-testid="transform-validation-errors"
        >
          {validationErrors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      {isFailed && dataset.error_message && (
        <div className="text-sm text-danger bg-danger-tint/10 rounded p-3 border border-danger-tint/30">
          {dataset.error_message}
        </div>
      )}

      {/* ===== Единый блок «Колонки» ===== */}
      {(() => {
        if (originalColumns.length === 0) return null;

        if (isReparsing) {
          return (
            <div className="card space-y-3" aria-busy="true">
              <div className="text-sm font-medium flex items-center gap-2 text-surface-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('files.transform.columns_updating', {
                  defaultValue: 'Обновляю список колонок…',
                })}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-9 bg-surface-800/60 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          );
        }

        const filtered = filter
          ? originalColumns.filter((c) =>
              c.toLowerCase().includes(filter.toLowerCase()),
            )
          : originalColumns;
        const selectedCount = selected.size;

        return (
          <div className="card space-y-3 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Pencil className="w-4 h-4" />
                {t('files.transform.columns_title', {
                  defaultValue: 'Колонки',
                })}
              </div>
              <span className="text-xs text-surface-500">
                {t('files.transform.columns_counter', {
                  defaultValue: 'выбрано {{count}} из {{total}}',
                  count: selectedCount,
                  total: originalColumns.length,
                })}
              </span>
              <div className="flex-1" />
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-surface-500 pointer-events-none" />
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t('files.transform.columns_search', {
                    defaultValue: 'Поиск…',
                  })}
                  className="bg-surface-800 border border-surface-700 rounded pl-7 pr-2 py-1 text-xs w-40"
                />
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setSelectedColumns(originalColumns)}
                  className="btn-ghost text-xs"
                  data-testid="transform-select-all"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedColumns([])}
                  className="btn-ghost text-xs"
                  data-testid="transform-select-none"
                >
                  None
                </button>
              </div>
            </div>

            {/* Список колонок: рабочий чекбокс + название (клик → инпут). */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5 max-h-[28rem] overflow-auto pr-1">
              {filtered.map((col) => {
                const effective = renames[col] || col;
                const isChecked = selected.has(effective);
                return (
                  <ColumnRow
                    key={col}
                    col={col}
                    effective={effective}
                    isChecked={isChecked}
                    initialRename={renames[col] ?? ''}
                    onToggle={(checked) => handleColumnToggle(effective, checked)}
                    onCommitRename={(newName) => handleRenameCommit(col, newName)}
                  />
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-xs text-surface-500 text-center py-4">
                  {t('files.transform.columns_empty', {
                    defaultValue: 'Ничего не найдено',
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ===== Превью-таблица ===== */}
      {(() => {
        if (isReparsing) {
          return (
            <div className="card space-y-2 min-w-0" aria-busy="true">
              <div className="text-sm font-medium flex items-center gap-2 text-surface-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('files.transform.preview_updating', {
                  defaultValue: 'Обновляю превью…',
                })}
              </div>
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-3 bg-surface-800/60 rounded animate-pulse"
                    style={{ width: `${85 - i * 4}%` }}
                  />
                ))}
              </div>
            </div>
          );
        }

        if (!colPreview || colPreview.rows.length === 0) return null;

        return (
          <div className="card space-y-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-medium flex items-center gap-2">
                <Check className="w-4 h-4 text-success" />
                {t('files.transform.preview_title', {
                  defaultValue: 'Превью',
                })}
                <span className="text-xs text-surface-500 font-normal">
                  ({colPreview.rows.length}{' '}
                  {t('files.transform.of', { defaultValue: 'из' })}{' '}
                  {dataset.total_rows})
                </span>
              </div>
              <div className="flex-1" />
              {/* Phase 2.4: preview limit selector 20/50/100/Все. */}
              <PreviewLimitSelector
                value={previewLimit}
                onChange={setPreviewLimit}
                total={dataset.total_rows}
              />
            </div>
            <div className="overflow-x-auto min-w-0 max-w-full">
              <DataTable
                columns={previewColumns}
                data={colPreview.rows}
                enablePinning
                pinLeftCount={0}
              />

            </div>
          </div>
        );
      })()}

      {/* Phase 2.2: inline download error */}
      {downloadError && (
        <div
          className="text-sm text-danger bg-danger-tint/10 rounded p-3 border border-danger-tint/30"
          data-testid="transform-download-error"
        >
          <span className="font-medium">
            {t('files.results.download_error_short', {
              defaultValue: 'Ошибка скачивания:',
            })}
          </span>{' '}
          {downloadError}
        </div>
      )}
    </div>
  );
}

// === Sub-components ===

function CompactMetric({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  tone?: 'default' | 'warn' | 'muted';
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 px-1.5 py-0.5 rounded border shrink-0',
        tone === 'warn' && 'border-warning-tint/30 bg-warning-tint/5',
        tone === 'muted' && 'border-surface-800 bg-panel',
        tone === 'default' && 'border-surface-800 bg-panel',
      )}
      title={label}
    >
      <Icon
        className={cn(
          'w-3 h-3 shrink-0',
          tone === 'warn' ? 'text-warning' : 'text-primary-400',
        )}
      />
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Phase 2.4: 20 / 50 / 100 / «Все» переключатель превью.
 * Использует нативный <select> для минимума зависимостей и сохранения
 * совместимости с E2E (Playwright).
 */
function PreviewLimitSelector({
  value,
  onChange,
  total,
}: {
  value: ColumnPreviewLimit;
  onChange: (next: ColumnPreviewLimit) => void;
  total: number;
}) {
  const { t } = useTranslation();
  return (
    <label className="text-[11px] flex items-center gap-1.5 text-surface-400">
      {t('files.transform.preview_limit', { defaultValue: 'Показать:' })}
      <div className="relative">
        <select
          className="bg-surface-800 border border-surface-700 rounded pl-2 pr-6 py-1 text-xs appearance-none cursor-pointer"
          value={value}
          onChange={(e) =>
            onChange(Number(e.target.value) as ColumnPreviewLimit)
          }
          data-testid="transform-preview-limit"
        >
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={0}>
            {t('files.transform.preview_limit_all', {
              defaultValue: 'Все',
            })}{' '}
            ({total.toLocaleString()})
          </option>
        </select>
        <ChevronDown className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 text-surface-500 pointer-events-none" />
      </div>
    </label>
  );
}

/**
 * Строка колонки: чекбокс + название, которое при клике превращается в инпут.
 *
 * Phase 2.4: rename — draft-only. Никаких PATCH на commit. Validation
 * (empty / duplicate) делается в parent через useMemo.
 */
function ColumnRow({
  col,
  effective,
  isChecked,
  initialRename,
  onToggle,
  onCommitRename,
}: {
  col: string;
  effective: string;
  isChecked: boolean;
  initialRename: string;
  onToggle: (next: boolean) => void;
  onCommitRename: (newName: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialRename);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Синхронизируем draft, когда приходят новые данные с сервера.
  useEffect(() => {
    if (!editing) setDraft(initialRename);
  }, [initialRename, editing]);

  // Phase 2.4: ставим курсор в конец строки при открытии редактирования.
  const startEditing = () => {
    setDraft(initialRename);
    setEditing(true);
    // inputRef устанавливается после первого рендера — defer через rAF.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    });
  };

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    // Пустое имя = revert к original (НЕ сохраняем пустой target в state).
    if (!trimmed) {
      if (initialRename && initialRename !== col) {
        // Был непустой rename — revert к original.
        onCommitRename('');
      }
      return;
    }
    if (trimmed !== (initialRename || '').trim()) {
      onCommitRename(trimmed);
    }
  };

  return (
    <div className="flex items-center gap-2 text-xs bg-panel rounded px-2 py-1.5">
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) => onToggle(e.target.checked)}
        className="shrink-0 cursor-pointer"
        aria-label={col}
        data-testid={`transform-col-checkbox-${col}`}
      />
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setDraft(initialRename);
              setEditing(false);
            }
          }}
          className="bg-surface-800 border border-primary-500 rounded px-2 py-0.5 flex-1 min-w-0 text-surface-100"
          data-testid={`transform-col-rename-input-${col}`}
        />
      ) : (
        <button
          type="button"
          onClick={startEditing}
          className={cn(
            'flex-1 text-left truncate cursor-text hover:bg-surface-800/60 rounded px-1 py-0.5',
            isChecked ? 'text-surface-200' : 'text-surface-500 line-through',
          )}
          title={col}
          data-testid={`transform-col-name-${col}`}
        >
          {effective}
        </button>
      )}
    </div>
  );
}
